import { IconType } from '../Icon/Icon';
import { UserMode } from '../../hooks/useApolloClient';
import { isImpersonating } from '../../services/auth';
import { ProfileFeature } from '../../hooks/useSessionInfo';

export type SidebarItem = {
  readonly text: string;
  readonly href: string;
  readonly icon?: IconType;
};

export type SidebarSection = {
  readonly label: string;
  readonly items: ReadonlyArray<SidebarItem>;
  readonly icon?: IconType;
};

export type SidebarEntry = SidebarItem | SidebarSection;

export const isSection = (entry: SidebarEntry): entry is SidebarSection =>
  'items' in entry;

const waterSupplierNav = (features: ProfileFeature[]): SidebarEntry[] => [
  {
    label: 'Backflow',
    icon: 'gauge',
    items: [
      { text: 'Dashboard', href: '', icon: 'dashboard' },
      { text: 'Approval', href: '/test-reports', icon: 'file' },
      { text: 'Jobs', href: '/jobs', icon: 'clipboard-check' },
      { text: 'Assemblies', href: '/assemblies', icon: 'wrench' },
      { text: 'Testers', href: '/testers', icon: 'people' },
    ],
  },
  ...(features.includes('WATER_HAZARDS')
    ? [
        {
          label: 'Hazard',
          icon: 'hazard' as IconType,
          items: [
            {
              text: 'Dashboard',
              href: '/hazard-dashboard',
              icon: 'dashboard' as IconType,
            },
            {
              text: 'Surveys',
              href: '/hazard-surveys',
              icon: 'file' as IconType,
            },
            { text: 'Sites', href: '/hazard-sites', icon: 'geo' as IconType },
            {
              text: 'Survey Templates',
              href: '/survey-templates',
              icon: 'file' as IconType,
            },
          ],
        },
      ]
    : []),
  {
    label: 'Accounts',
    icon: 'droplet',
    items: [
      { text: 'Water Services', href: '/water-services', icon: 'droplet' },
      { text: 'Customers', href: '/customers', icon: 'customer' },
    ],
  },
  { text: 'Notices', href: '/notices', icon: 'mail' },
  {
    label: 'Settings',
    icon: 'settings',
    items: [
      { text: 'Profile', href: '/profile', icon: 'person' },
      { text: 'Users', href: '/users', icon: 'people' },
      {
        text: 'Notifications',
        href: '/notification-settings',
        icon: 'bell',
      },
      // { text: 'Email Delivery', href: '/email-settings', icon: 'mail' },
      // { text: 'Billing', href: '/billing', icon: 'billing' },
    ],
  },
];

const companyNav = (): SidebarEntry[] => [
  {
    label: 'Backflow',
    icon: 'gauge',
    items: [
      { text: 'Jobs', href: '/jobs', icon: 'clipboard-check' },
      { text: 'Assemblies', href: '/assemblies', icon: 'wrench' },
      { text: 'Testers', href: '/testers', icon: 'people' },
    ],
  },
  { text: 'Customers', href: '/customers', icon: 'customer' },
  ...(isImpersonating()
    ? [{ text: 'Notices', href: '/notices', icon: 'notices' as IconType }]
    : []),
  {
    label: 'Settings',
    icon: 'settings',
    items: [
      { text: 'Profile', href: '/profile', icon: 'person' },
      { text: 'Users', href: '/users', icon: 'people' },
      {
        text: 'Notifications',
        href: '/notification-settings',
        icon: 'bell',
      },
      // { text: 'Email Delivery', href: '/email-settings', icon: 'mail' },
      // { text: 'Billing', href: '/billing', icon: 'billing' },
    ],
  },
];

const adminNav: SidebarEntry[] = [
  { text: 'Dashboard', href: '', icon: 'dashboard' },
  { text: 'Companies', href: '/companies', icon: 'people' },
  { text: 'Water Suppliers', href: '/water-suppliers', icon: 'droplet' },
  { text: 'Users', href: '/users', icon: 'person' },
  { text: 'Makes & Models', href: '/makes', icon: 'wrench' },
  { text: 'Plans', href: '/plans', icon: 'billing' },
  { text: 'Water Hazards', href: '/water-hazards', icon: 'hazard' },
  { text: 'Survey Templates', href: '/survey-templates', icon: 'file' },
  {
    label: 'Settings',
    icon: 'settings',
    items: [
      {
        text: 'Notifications',
        href: '/notification-settings',
        icon: 'bell',
      },
    ],
  },
];

const individualNav: SidebarEntry[] = [
  { text: 'Jobs', href: '/jobs', icon: 'clipboard-check' },
  {
    label: 'Settings',
    icon: 'settings',
    items: [
      { text: 'Profile', href: '/profile', icon: 'person' },
      {
        text: 'Notifications',
        href: '/notification-settings',
        icon: 'bell',
      },
    ],
  },
];

export const navigationConfig = (
  mode: UserMode,
  features: ProfileFeature[] = []
): SidebarEntry[] =>
  ({
    'water-supplier': waterSupplierNav(features),
    company: companyNav(),
    admin: adminNav,
    individual: individualNav,
  })[mode];
